/**
 * Serviço de API do Sistema MEDICARE
 * Configuração do Axios para comunicação com o backend
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError } from '../types';

// Configuração base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Criação da instância do Axios
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 segundos
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Interceptor de requisição
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Adicionar token de autenticação se existir
    const token = localStorage.getItem('medicare_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log da requisição em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor de resposta
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log da resposta em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
      
      // Log especial para consultas - mostrar retorno completo na tela
      if (response.config.url?.includes('/consultas') && response.config.method === 'post') {
        console.log('🔍 RETORNO COMPLETO DA API DE CONSULTAS:');
        console.log('Status:', response.status);
        console.log('Headers:', response.headers);
        console.log('Data completo:', JSON.stringify(response.data, null, 2));
        
        // Mostrar na tela também
        const alertMessage = `RETORNO COMPLETO DA API:\n\nStatus: ${response.status}\n\nData: ${JSON.stringify(response.data, null, 2)}`;
        setTimeout(() => {
          alert(alertMessage);
        }, 100);
      }
    }

    return response;
  },
  (error: AxiosError) => {
    // Log do erro
    console.error('❌ API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data,
    });

    // Tratamento de erros específicos
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('medicare_token');
      delete api.defaults.headers.common['Authorization'];
      
      // Redirecionar para login se não estiver na página de login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // Padronizar erro para o frontend
    const apiError: ApiError = {
      message: (error.response?.data as any)?.message || error.message || 'Erro desconhecido',
      code: (error.response?.data as any)?.code || error.code,
      details: (error.response?.data as any)?.details,
    };

    return Promise.reject(apiError);
  }
);

// Classe para gerenciar requisições da API
export class ApiService {
  // Método GET genérico
  static async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await api.get(url, config);
      
      // Verificar se a resposta tem a estrutura padrão do backend
      if (response.data && typeof response.data === 'object' && 'sucesso' in response.data) {
        return {
          success: response.data.sucesso,
          data: response.data, // Retornar a resposta completa para manter compatibilidade
          error: response.data.sucesso ? undefined : { message: response.data.mensagem }
        };
      }
      
      // Fallback para respostas diretas
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método POST genérico
  static async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await api.post<T>(url, data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método PUT genérico
  static async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      // Log específico para atualização de perfil
      if (url.includes('/usuarios/perfil')) {
        console.log('🔄 ApiService PUT - Atualizando perfil:', {
          url,
          data,
          timestamp: new Date().toISOString()
        });
      }
      
      const response = await api.put<T>(url, data, config);
      
      // Log específico para resposta de atualização de perfil
      if (url.includes('/usuarios/perfil')) {
        console.log('✅ ApiService PUT - Perfil atualizado:', {
          status: response.status,
          data: response.data,
          timestamp: new Date().toISOString()
        });
      }
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método PATCH genérico
  static async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await api.patch<T>(url, data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método DELETE genérico
  static async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await api.delete<T>(url, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método para upload de arquivos
  static async upload<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<T>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ApiError,
      };
    }
  }

  // Método para download de arquivos
  static async download(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      const response = await api.get(url, {
        ...config,
        responseType: 'blob',
      });

      // Criar URL do blob
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);

      // Criar link temporário para download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpar URL do blob
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erro no download:', error);
      throw error;
    }
  }

  // Método para verificar conectividade
  static async checkHealth(): Promise<boolean> {
    try {
      const response = await api.get('/health');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  // Método para cancelar requisições
  static createCancelToken() {
    return axios.CancelToken.source();
  }

  // Método para verificar se o erro é de cancelamento
  static isCancelError(error: any): boolean {
    return axios.isCancel(error);
  }
}

// Função utilitária para construir query strings
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, String(item)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });
  
  return searchParams.toString();
};

// Função utilitária para tratar erros de validação
export const handleValidationErrors = (error: ApiError): Record<string, string> => {
  const errors: Record<string, string> = {};
  
  if (error.details && Array.isArray(error.details)) {
    error.details.forEach((detail: any) => {
      if (detail.field && detail.message) {
        errors[detail.field] = detail.message;
      }
    });
  }
  
  return errors;
};

// Função utilitária para retry de requisições
export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  }
  
  throw lastError;
};

export default api;